import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

interface WebhookLog {
  id: string;
  gateway: string;
  event_id: string | null;
  action: string | null;
  type: string | null;
  data_id: string | null;
  live_mode: boolean | null;
  raw_payload: string | null;
  processing_status: string;
  processing_notes: string | null;
  mp_payment_status: string | null;
  mp_status_detail: string | null;
  external_reference: string | null;
  created_at: string;
}

const STATUS_META: Record<string, { label: string; color: string; icon: string }> = {
  received:             { label: 'Recebido',        color: 'bg-sky-500/20 text-sky-400',     icon: 'ri-download-line' },
  processed:            { label: 'Processado',      color: 'bg-emerald-500/20 text-emerald-400', icon: 'ri-checkbox-circle-line' },
  skipped_test:         { label: 'Teste/Simulador', color: 'bg-yellow-500/20 text-yellow-400',   icon: 'ri-flask-line' },
  skipped_duplicate:    { label: 'Duplicado',       color: 'bg-white/10 text-white/40',          icon: 'ri-repeat-line' },
  skipped_non_payment:  { label: 'Ignorado',        color: 'bg-white/10 text-white/40',          icon: 'ri-skip-forward-line' },
  skipped_no_id:        { label: 'Sem ID',          color: 'bg-orange-500/20 text-orange-400',   icon: 'ri-error-warning-line' },
  skipped_no_gateway:   { label: 'Sem gateway',     color: 'bg-orange-500/20 text-orange-400',   icon: 'ri-server-line' },
  skipped_unknown_ref:  { label: 'Ref. desconhecida', color: 'bg-amber-500/20 text-amber-400',   icon: 'ri-question-line' },
  error:                { label: 'Erro',            color: 'bg-red-500/20 text-red-400',         icon: 'ri-close-circle-line' },
};

const MP_PAYMENT_STATUS_META: Record<string, { label: string; color: string }> = {
  approved:   { label: 'Aprovado',  color: 'text-emerald-400' },
  pending:    { label: 'Pendente',  color: 'text-yellow-400' },
  in_process: { label: 'Em análise', color: 'text-yellow-400' },
  rejected:   { label: 'Recusado', color: 'text-red-400' },
  cancelled:  { label: 'Cancelado', color: 'text-white/40' },
};

const FILTERS = [
  { label: 'Todos', value: 'all' },
  { label: 'Processados', value: 'processed' },
  { label: 'Testes/Simulador', value: 'skipped_test' },
  { label: 'Erros', value: 'error' },
  { label: 'Ignorados', value: 'skipped' },
];

const LIVE_FILTERS = [
  { label: 'Todos', value: 'all' },
  { label: 'Produção (live)', value: 'live' },
  { label: 'Sandbox/Teste', value: 'sandbox' },
];

const AdminWebhooksPage = () => {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLiveMode, setFilterLiveMode] = useState('all');
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchLogs = useCallback(async () => {
    const { data } = await supabase
      .from('webhook_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);
    setLogs((data as WebhookLog[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchLogs, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('webhook-logs-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'webhook_logs' }, (payload) => {
        setLogs((prev) => [payload.new as WebhookLog, ...prev].slice(0, 200));
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'webhook_logs' }, (payload) => {
        setLogs((prev) => prev.map((l) => l.id === payload.new.id ? payload.new as WebhookLog : l));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const fmtDT = (d: string) =>
    new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const filtered = logs.filter((l) => {
    if (filterLiveMode === 'live' && l.live_mode !== true) return false;
    if (filterLiveMode === 'sandbox' && l.live_mode !== false && l.live_mode !== null) return false;
    if (filterStatus === 'all') return true;
    if (filterStatus === 'skipped') return l.processing_status.startsWith('skipped_');
    return l.processing_status === filterStatus;
  });

  // Summary counts
  const total     = logs.length;
  const processed = logs.filter((l) => l.processing_status === 'processed').length;
  const testCount = logs.filter((l) => l.processing_status === 'skipped_test').length;
  const errCount  = logs.filter((l) => l.processing_status === 'error').length;
  const liveCount = logs.filter((l) => l.live_mode === true).length;
  const sandboxCount = logs.filter((l) => l.live_mode === false || l.live_mode === null).length;

  const formatPayload = (raw: string | null) => {
    if (!raw) return '—';
    try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-[24px] font-700 text-white">Logs de Webhook</h1>
          <p className="text-white/40 text-[14px] mt-1">Todos os eventos recebidos do Mercado Pago</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchLogs}
            className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/60 text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-[14px]" />
            Atualizar
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${
              autoRefresh
                ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${autoRefresh ? 'bg-emerald-400 animate-pulse' : 'bg-white/30'}`} />
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total recebidos', value: total, icon: 'ri-notification-3-line', color: 'bg-sky-600' },
          { label: 'Processados', value: processed, icon: 'ri-checkbox-circle-line', color: 'bg-emerald-600' },
          { label: 'Teste/Simulador', value: testCount, icon: 'ri-flask-line', color: 'bg-yellow-600' },
          { label: 'Erros', value: errCount, icon: 'ri-error-warning-line', color: 'bg-red-600' },
          { label: 'Live / Sandbox', value: `${liveCount} / ${sandboxCount}`, icon: 'ri-global-line', color: 'bg-violet-600' },
        ].map((c) => (
          <div key={c.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${c.color}`}>
              <i className={`${c.icon} text-white text-[16px]`} />
            </div>
            <div>
              <p className="text-[11px] text-white/30 mb-0.5">{c.label}</p>
              <p className="text-[18px] font-700 text-white">{c.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Distinction info card */}
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-4 mb-5 flex items-start gap-3">
        <i className="ri-information-line text-yellow-400 text-[18px] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-[13px] font-700 text-yellow-300 mb-1">Como diferenciar evento de teste de pagamento real</p>
          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <div>
              <p className="text-yellow-200 font-600 mb-1">Evento de teste / Simulador:</p>
              <ul className="text-white/50 space-y-0.5 list-disc list-inside">
                <li>Status: <span className="text-yellow-400 font-600">Teste/Simulador</span></li>
                <li>MP retorna 404 para o payment_id</li>
                <li>Geralmente <code className="bg-white/10 px-1 rounded">live_mode: false</code></li>
                <li><strong>Não cria registro</strong> em Gestão de Pagamentos</li>
              </ul>
            </div>
            <div>
              <p className="text-emerald-300 font-600 mb-1">Pagamento real (sandbox ou produção):</p>
              <ul className="text-white/50 space-y-0.5 list-disc list-inside">
                <li>Status: <span className="text-emerald-400 font-600">Processado</span></li>
                <li>MP retorna dados reais do pagamento</li>
                <li>external_reference no formato <code className="bg-white/10 px-1 rounded">user_id|plan_id|cycle</code></li>
                <li><strong>Cria registro</strong> em Gestão de Pagamentos</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5 flex gap-4 flex-wrap items-center">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600">Status</span>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilterStatus(f.value)}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${
                filterStatus === f.value ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600">Modo</span>
          {LIVE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilterLiveMode(f.value)}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${
                filterLiveMode === f.value ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/30">
            <i className="ri-notification-off-line text-3xl mb-2 block" />
            <p className="text-[14px]">Nenhum evento de webhook registrado</p>
            <p className="text-[12px] mt-1">Eventos aparecerão aqui assim que o Mercado Pago enviar notificações</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead>
                <tr className="border-b border-white/10">
                  {['Data/Hora', 'Payment ID', 'Action', 'Status processamento', 'Status MP', 'Live Mode', 'Ref. Externa', 'Detalhe'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-600 text-white/30 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => {
                  const statusMeta = STATUS_META[log.processing_status] ?? { label: log.processing_status, color: 'bg-white/10 text-white/40', icon: 'ri-question-line' };
                  const mpMeta = log.mp_payment_status ? (MP_PAYMENT_STATUS_META[log.mp_payment_status] ?? null) : null;
                  const isTest = log.processing_status === 'skipped_test';
                  const isProcessed = log.processing_status === 'processed';

                  return (
                    <tr
                      key={log.id}
                      className={`border-b border-white/5 transition-colors ${
                        isTest ? 'bg-yellow-500/5' : isProcessed ? 'bg-emerald-500/[0.03]' : ''
                      } hover:bg-white/[0.04]`}
                    >
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <p className="text-[12px] text-white/60">{fmtDT(log.created_at)}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        {log.data_id ? (
                          <span className="text-[12px] font-mono text-white/70">{log.data_id}</span>
                        ) : (
                          <span className="text-[12px] text-white/20">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-[12px] text-white/50 font-mono">{log.action || log.type || '—'}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <i className={`${statusMeta.icon} text-[13px] ${statusMeta.color.split(' ')[1] || 'text-white/40'}`} />
                          <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${statusMeta.color}`}>
                            {statusMeta.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        {mpMeta ? (
                          <span className={`text-[12px] font-600 ${mpMeta.color}`}>{mpMeta.label}</span>
                        ) : (
                          <span className="text-[12px] text-white/20">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {log.live_mode === true && (
                          <span className="text-[11px] font-700 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">LIVE</span>
                        )}
                        {log.live_mode === false && (
                          <span className="text-[11px] font-700 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full">SANDBOX</span>
                        )}
                        {log.live_mode === null && (
                          <span className="text-[11px] text-white/20">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 max-w-[180px]">
                        {log.external_reference ? (
                          <span className="text-[11px] text-white/40 font-mono truncate block">{log.external_reference}</span>
                        ) : (
                          <span className="text-[12px] text-white/20">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="w-7 h-7 flex items-center justify-center bg-white/5 hover:bg-white/15 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-eye-line text-white/50 text-[13px]" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-5 py-3 border-t border-white/5 text-[12px] text-white/30">
          {filtered.length} evento{filtered.length !== 1 ? 's' : ''} exibido{filtered.length !== 1 ? 's' : ''}
          {logs.length !== filtered.length && ` (de ${logs.length} total)`}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#1a1a2e]">
              <div>
                <h2 className="text-[16px] font-700 text-white">Detalhe do Evento</h2>
                <p className="text-[12px] text-white/30 font-mono mt-0.5">{selectedLog.id}</p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-white/60" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Status badge */}
              {(() => {
                const meta = STATUS_META[selectedLog.processing_status] ?? { label: selectedLog.processing_status, color: 'bg-white/10 text-white/40', icon: 'ri-question-line' };
                return (
                  <div className={`flex items-center gap-2 p-3 rounded-xl ${selectedLog.processing_status === 'skipped_test' ? 'bg-yellow-500/10 border border-yellow-500/20' : selectedLog.processing_status === 'processed' ? 'bg-emerald-500/10 border border-emerald-500/20' : selectedLog.processing_status === 'error' ? 'bg-red-500/10 border border-red-500/20' : 'bg-white/5 border border-white/10'}`}>
                    <i className={`${meta.icon} text-[18px] ${meta.color.split(' ')[1] || 'text-white/40'}`} />
                    <div>
                      <p className="text-[13px] font-700 text-white">{meta.label}</p>
                      {selectedLog.processing_notes && (
                        <p className="text-[12px] text-white/50 mt-0.5">{selectedLog.processing_notes}</p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Gateway', value: selectedLog.gateway },
                  { label: 'Notification ID', value: selectedLog.event_id || '—' },
                  { label: 'Action', value: selectedLog.action || '—' },
                  { label: 'Type', value: selectedLog.type || '—' },
                  { label: 'Payment ID (data.id)', value: selectedLog.data_id || '—' },
                  { label: 'Live Mode', value: selectedLog.live_mode === true ? 'true (Produção)' : selectedLog.live_mode === false ? 'false (Sandbox/Teste)' : '—' },
                  { label: 'Status MP', value: selectedLog.mp_payment_status || '—' },
                  { label: 'Status Detail MP', value: selectedLog.mp_status_detail || '—' },
                  { label: 'External Reference', value: selectedLog.external_reference || '—' },
                  { label: 'Recebido em', value: new Date(selectedLog.created_at).toLocaleString('pt-BR') },
                ].map((row) => (
                  <div key={row.label} className="bg-white/5 rounded-xl p-3">
                    <p className="text-[11px] text-white/30 mb-1">{row.label}</p>
                    <p className="text-[12px] text-white font-500 break-all font-mono">{row.value}</p>
                  </div>
                ))}
              </div>

              {/* Raw payload */}
              {selectedLog.raw_payload && (
                <div>
                  <p className="text-[12px] text-white/40 font-600 uppercase tracking-wider mb-2">Payload bruto</p>
                  <pre className="bg-black/30 border border-white/10 rounded-xl p-3 text-[11px] text-white/60 font-mono overflow-x-auto whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto">
                    {formatPayload(selectedLog.raw_payload)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWebhooksPage;
